import React, { useLayoutEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { JPInputAutoComplete } from "@companion-core/shared/app/Components/Inputs/JPInputAutoComplete/JPInputAutoComplete";
import { GooglePlaceAddress } from "@companion-core/shared/app/Interfaces/adresses";
import { getAddress } from "@companion-core/shared/app/Utils/apiClient";
import { JPInputAutocompleteFilterProps } from "./JPInputAutocompleteFilter.model";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";

import "@companion-core/web/src/Assets/Styles/scss/inputAutocompleteFilter.scss";

/* eslint-disable camelcase -- aligné sur l’interface GooglePlaceAddress (snake_case API) */
const placeholderAddress = (formattedAddress: string): GooglePlaceAddress => ({
  formatted_address: formattedAddress,
  name: null,
  city: "",
  country_code: "",
  latitude: null,
  longitude: null,
  state_code: "",
  timezone: "",
  type: "",
});
/* eslint-enable camelcase */

export const JPInputAutocompleteFilter = ({
  testID,
  initialValue,
  initialLabel,
  onValueChange,
  addressType,
}: JPInputAutocompleteFilterProps) => {
  const [selectedAddress, setSelectedAddress] = useState<GooglePlaceAddress | null>(null);
  const selectedPlaceIdRef = useRef<string | null>(null);
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!initialValue?.placeId) {
      setSelectedAddress(null);
      selectedPlaceIdRef.current = null;
      return;
    }
    if (selectedPlaceIdRef.current === initialValue.placeId) {
      return;
    }
    let cancelled = false;
    if (initialLabel) {
      setSelectedAddress((prev) =>
        prev?.formatted_address === initialLabel ? prev : placeholderAddress(initialLabel)
      );
    }
    getAddress(initialValue.placeId, undefined, addressType).then(({ response: address }) => {
      if (cancelled) return;
      if (address) {
        selectedPlaceIdRef.current = initialValue.placeId;
        setSelectedAddress(address);
      } else if (!initialLabel) {
        setSelectedAddress(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [initialValue?.placeId, initialValue?.placeLevel, initialLabel, addressType]);

  const handleOptionSelect = (
    address: GooglePlaceAddress,
    placeData: { placeId: string; placeLevel: string }
  ) => {
    setSelectedAddress(address);
    selectedPlaceIdRef.current = placeData.placeId;
    onValueChange?.({
      "geo_place_id": placeData.placeId,
      "geo_place_level": placeData.placeLevel,
      label: address.formatted_address,
    });
  };

  return (
    <div className="input-autocomplete-filter">
      <JPInputAutoComplete
        testID={testID}
        onOptionSelect={handleOptionSelect}
        addressType={addressType}
        selectWithoutFillingInput
        isStateless
        inputIcon="JP-Location"
        placeholder={t("common.filters.autocomplete.placeholder")}
        name="search"
      />
      {selectedAddress && (
        <div className="input-autocomplete-filter__selected-address">
          <span className="input-autocomplete-filter__selected-address-text">
            {selectedAddress.formatted_address}
          </span>
          <button
            className="input-autocomplete-filter__selected-address-close"
            onClick={() => {
              setSelectedAddress(null);
              selectedPlaceIdRef.current = null;
              onValueChange?.(undefined);
            }}>
            <FontIcon name="JP-Close-bold" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
